import { useMemo } from 'react';
import { CodeBlock, Icon } from '../../components/ui';
import { Callout, DocSection, Endpoint, EndpointList, LangCode, More, ParamTable, type Param } from '../../components/DocBlocks';
import { BASE_URL, LANGS, VIDEO_LANGS, imageSnippet, videoSnippet, type Lang } from '../../snippets';
import { PageHead, pretty, round2, useDocs } from './context';

/* Gambar */

const IMAGE_MAIN: Param[] = [
  { name: 'model', type: 'string', required: true, desc: 'ID model gambar, misalnya seri Seedream.' },
  { name: 'prompt', type: 'string', required: true, desc: 'Deskripsi gambar, sampai 32.000 karakter.' },
  { name: 'n', type: 'integer', desc: 'Jumlah gambar, 1 sampai 4 (dibatasi model). Bawaannya 1.' },
  {
    name: 'size',
    type: 'string',
    desc: (
      <>
        Ukuran gambar, diteruskan apa adanya. Misalnya <code>1K</code>, <code>2K</code>, atau <code>2048x2048</code>.
      </>
    ),
  },
];

const IMAGE_MORE: Param[] = [
  {
    name: 'response_format',
    type: 'string',
    desc: (
      <>
        <code>url</code> (bawaan) atau <code>b64_json</code>. URL dari provider berlaku 24 jam.
      </>
    ),
  },
  { name: 'image', type: 'string atau string[]', desc: 'Gambar referensi untuk diedit: URL atau data URI, maksimal 10.' },
  {
    name: 'output_format / background / quality / seed / watermark / user',
    type: 'beragam',
    desc: 'Diteruskan bila provider mendukung.',
  },
];

export function ImagesPage() {
  const { lang, setLang, imageModel, image } = useDocs();

  const example = useMemo(() => {
    const amount = image?.pricing?.per_image ? image.pricing.per_image * 2 : 765.18;
    return pretty({
      id: 'req_01M3K7…',
      created: 1790950000,
      model: imageModel,
      data: [{ url: 'https://…/1.png' }, { url: 'https://…/2.png' }],
      usage: { images: 2 },
      billing: { currency: 'IDR', amount: round2(amount), pricing_version_id: '…' },
    });
  }, [imageModel, image]);

  return (
    <>
      <PageHead slug="gambar">
        Buat gambar dari teks, atau edit gambar referensi. Hasilnya langsung dikirim di respons yang sama.
      </PageHead>

      <DocSection id="request" title="Kirim request">
        <Endpoint method="POST" path="/v1/images/generations" note="Sinkron" />
        <p>
          Bentuknya sama dengan OpenAI, jadi <code>client.images.generate()</code> langsung jalan.
        </p>
        <LangCode code={imageSnippet(lang, { model: imageModel })} lang={lang} langs={LANGS} onLang={setLang} />
      </DocSection>

      <DocSection id="parameter" title="Parameter">
        <ParamTable params={IMAGE_MAIN} />
        <More label="Parameter lainnya" count={IMAGE_MORE.length}>
          <ParamTable params={IMAGE_MORE} />
        </More>
      </DocSection>

      <DocSection id="respons" title="Respons">
        <CodeBlock label="200 · application/json" code={example} />
        <p>
          Saldo ditahan untuk <code>n</code> gambar, tetapi yang ditagih hanya gambar yang benar-benar jadi.
        </p>
        <p>
          Model gambar hanya menjawab di endpoint ini. Bila dikirim ke <code>/v1/chat/completions</code>, pesan error 400 menyebut
          endpoint yang benar.
        </p>
      </DocSection>
    </>
  );
}

/* Video */

const VIDEO_MAIN: Param[] = [
  { name: 'model', type: 'string', required: true, desc: 'ID model video, misalnya seri Seedance.' },
  { name: 'prompt', type: 'string', required: true, desc: 'Deskripsi adegan, sampai 4.000 karakter.' },
  {
    name: 'seconds',
    type: 'integer',
    desc: (
      <>
        Durasi dalam detik. Bawaannya 5, dibatasi model. Nama lain: <code>duration</code>.
      </>
    ),
  },
  {
    name: 'resolution',
    type: 'string',
    desc: (
      <>
        <code>480p</code>, <code>720p</code> (bawaan), <code>1080p</code>, atau <code>4k</code> pada model tertentu.
      </>
    ),
  },
  {
    name: 'aspect_ratio',
    type: 'string',
    desc: (
      <>
        <code>16:9</code> (bawaan), <code>9:16</code>, <code>1:1</code>, <code>4:3</code>, <code>3:4</code>, <code>21:9</code>, atau{' '}
        <code>adaptive</code> bila memakai frame awal.
      </>
    ),
  },
];

const VIDEO_MORE: Param[] = [
  {
    name: 'frame_images',
    type: 'array',
    desc: (
      <>
        Frame awal dan akhir untuk membuat video dari gambar, maksimal 2. Contoh:{' '}
        <code>{'[{"url": "…", "frame_type": "first_frame"}]'}</code>. Nilai lainnya <code>last_frame</code>.
      </>
    ),
  },
  { name: 'input_references', type: 'array', desc: 'Gambar referensi gaya atau objek, maksimal 9.' },
  { name: 'generate_audio / seed / watermark', type: 'beragam', desc: 'Diteruskan ke provider.' },
];

export function VideoPage() {
  const { lang, setLang, videoModel, video } = useDocs();
  const videoLang: Lang = lang === 'php' ? 'curl' : lang;

  const example = useMemo(() => {
    const amount = video?.pricing?.per_1m_video_tokens ? (108000 * video.pricing.per_1m_video_tokens) / 1e6 : 17675.66;
    return pretty({
      id: 'vid_01M3…',
      object: 'video',
      model: videoModel,
      status: 'completed',
      prompt: 'Ombak di Pantai Kuta saat senja',
      seconds: 5,
      resolution: '720p',
      aspect_ratio: '16:9',
      created_at: 1790950000,
      completed_at: 1790950062,
      expires_at: 1791036462,
      url: `${BASE_URL}/videos/vid_01M3…/content`,
      error: null,
      usage: { video_tokens: 108000 },
      billing: { currency: 'IDR', amount: round2(amount), pricing_version_id: '…' },
    });
  }, [videoModel, video]);

  return (
    <>
      <PageHead slug="video">
        Video dibuat di latar belakang, biasanya 30 detik sampai beberapa menit. Anda membuat job, menunggu, lalu mengunduh
        hasilnya.
      </PageHead>

      <DocSection id="cara-kerja" title="Cara kerja">
        <p>
          <code>POST /v1/videos</code> langsung membalas 202 dengan status <code>queued</code>. Cek statusnya tiap 5 detik atau
          lebih sampai selesai.
        </p>
        <p className="sr-only">
          Status job: queued, lalu in_progress, lalu completed. Job juga bisa berakhir failed, cancelled, atau expired.
        </p>
        <div className="docs-flow" aria-hidden>
          <span className="badge">
            queued
          </span>
          <Icon name="right" size={14} className="docs-flow-arrow" />
          <span className="badge badge-warn">
            in_progress
          </span>
          <Icon name="right" size={14} className="docs-flow-arrow" />
          <span className="badge badge-good">
            completed
          </span>
          <span className="docs-flow-alt">
            <span className="muted">atau berakhir</span>
            <span className="badge badge-critical">
              failed
            </span>
            <span className="badge">
              cancelled
            </span>
            <span className="badge">
              expired
            </span>
          </span>
        </div>
        <EndpointList
          items={[
            { method: 'POST', path: '/v1/videos', note: 'Buat job.' },
            { method: 'GET', path: '/v1/videos/{id}', note: 'Status terbaru.' },
            { method: 'GET', path: '/v1/videos/{id}/content', note: 'Unduh file. 409 bila belum selesai, 410 bila kedaluwarsa.' },
            { method: 'DELETE', path: '/v1/videos/{id}', note: 'Batalkan job yang masih queued. Saldo kembali penuh.' },
            { method: 'GET', path: '/v1/videos', note: 'Video terbaru organisasi Anda.' },
          ]}
        />
      </DocSection>

      <DocSection id="contoh" title="Buat, tunggu, unduh">
        <LangCode code={videoSnippet(videoLang, { model: videoModel })} lang={videoLang} langs={VIDEO_LANGS} onLang={setLang} />
        <Callout title="File tersedia 24 jam" tone="warn">
          Setelah <code>expires_at</code>, <code>/content</code> membalas 410. Unduh dan simpan video di penyimpanan Anda sendiri.
        </Callout>
      </DocSection>

      <DocSection id="parameter" title="Parameter">
        <ParamTable params={VIDEO_MAIN} />
        <More label="Parameter lainnya" count={VIDEO_MORE.length}>
          <ParamTable params={VIDEO_MORE} />
        </More>
        <p className="docs-caption">Seedance tidak menerima foto wajah manusia asli sebagai gambar referensi.</p>
      </DocSection>

      <DocSection id="objek" title="Objek video">
        <CodeBlock label="200 · application/json" code={example} />
        <p>
          <code>GET /v1/videos/{'{id}'}/content</code> mengalihkan (302) ke file video. Link ini juga ada di field{' '}
          <code>url</code>.
        </p>
      </DocSection>

      <DocSection id="biaya" title="Biaya video">
        <p>
          Video dihitung per token video. Jumlahnya bergantung pada resolusi dan durasi. Saat job dibuat, kami menahan perkiraan
          biaya terbesarnya. Setelah selesai, tagihan mengikuti <code>usage.video_tokens</code> dari provider.
        </p>
        <p>
          Job yang gagal atau dibatalkan mengembalikan saldo yang ditahan sepenuhnya. Job tetap diselesaikan dan ditagih walau
          statusnya tidak pernah Anda cek.
        </p>
      </DocSection>
    </>
  );
}
